import React, { type ReactNode, forwardRef } from 'react';
import { Link, LinkProps } from 'react-router-dom';

import { LoadingButton } from '@mui/lab';
import { CircularProgress, ButtonProps as MaterialButtonProps } from '@mui/material';

import { mapButtonSizeToLoaderSize } from './utils/mapButtonSizeToLoaderSize';

export type ButtonProps = Omit<
  Pick<
    MaterialButtonProps,
    | 'children'
    | 'disabled'
    | 'fullWidth'
    | 'href'
    | 'size'
    | 'startIcon'
    | 'endIcon'
    | 'sx'
    | 'type'
    | 'variant'
    | 'tabIndex'
    | 'onClick'
    | 'ref'
    | 'form'
    | 'disableRipple'
  >,
  'ref'
> & {
  color?: 'primary' | 'secondary' | 'info' | 'error';
  externalHref?: string;
  isLoading?: boolean;
  loadingIndicator?: ReactNode;
};

// This is necessary because otherwise we get a console error from MUI as it uses ref
const LinkComponent = React.forwardRef<HTMLAnchorElement, LinkProps>(({ to, ...props }, ref) => (
  <Link innerRef={ref} to={to} {...props} />
));

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      externalHref,
      href,
      isLoading = false,
      loadingIndicator,
      size = 'medium',
      variant = 'contained',
      ...props
    },
    ref
  ) => {
    const loadingButtonProps = {
      ref,
      href: externalHref,
      loadingIndicator: loadingIndicator || <CircularProgress color="inherit" size={mapButtonSizeToLoaderSize(size)} />,
      LinkComponent: href ? LinkComponent : undefined,
      loading: isLoading,
      size,
      target: externalHref ? '_blank' : undefined,
      to: href,
      variant,
      ...props,
    };

    return (
      <LoadingButton {...loadingButtonProps}>
        {/**
         * This is required because MUI renders the children even when loading
         * and this way the width of the button does not match the designs
         */}
        {!isLoading ? children : null}
      </LoadingButton>
    );
  }
);
